// src/utils/sellerDoorCode.js
// The Seller Provided Door Code on a listing, and the one rule every email follows: when a listing has a door
// code, the email sends the door code INSTEAD of the lockbox.
//
// Henry, 2026-09-18: "if there is a front door code, that can be an option as well that overrides lockbox ...
// just enter a code for emails for open houses ETC". He picked Option B from the mockup: the lockbox works as
// before, and a separate door code box sits under it. Spec: docs/superpowers/specs/2026-09-18-seller-door-code-
// design.md.
//
// Stored on the deal (and copied onto each open house) as `sellerDoorCode`, free text so it can carry steps
// ("4417 then #, side door"). Never mixed with the seller's portal Information Transfer "Door Keypad Code",
// which is for the buyers at closing.
//
// Pure and import-free on purpose: the listing page, the open house emails and the Settings preview all import
// it, and openHouseEmails.js must be able to import it without a cycle. The server (functions-api/
// ohRequestEmails.js) carries its own twin of accessSection; keep the two in step.

export const DOOR_CODE_LABEL = "Door Code";
export const LOCKBOX_LABEL = "Lockbox";

// The door code a deal or open house carries: trimmed text, or "" when there is none. Only a string or a
// number counts, so a stray object or boolean can never reach an email.
export function doorCodeOf(record) {
  const raw = record && typeof record === "object" ? record.sellerDoorCode : undefined;
  if (typeof raw !== "string" && typeof raw !== "number") return "";
  return String(raw).trim();
}

export function hasDoorCode(record) {
  return doorCodeOf(record) !== "";
}

// The access section of a hosting-agent email: { label, value }, or null when there is nothing to say.
// `lockboxValue` is what the email would have shown for the lockbox alone (openHouseEmails.lockboxSectionValue).
// A door code wins; otherwise the lockbox line is unchanged from before.
export function accessSection(lockboxValue, record) {
  const code = doorCodeOf(record);
  if (code) return { label: DOOR_CODE_LABEL, value: code };
  const lockbox = typeof lockboxValue === "string" ? lockboxValue.trim() : "";
  return lockbox ? { label: LOCKBOX_LABEL, value: lockbox } : null;
}

const DOOR_CODE_PLACEHOLDER_RE = /\{\{\s*doorCode\s*\}\}/;
const LOCKBOX_PLACEHOLDER_RE = /\{\{\s*lockbox\s*\}\}/;

// Placeholder values for a template that may carry {{lockbox}}, {{lockboxType}}, {{lockboxNumber}},
// {{lockboxCode}} and {{doorCode}} (the vendor emails and any custom template).
//   lockboxFields: the lockbox values the caller already worked out, Electronic blanking included:
//                  { lockboxType, lockboxNumber, lockboxCode, lockbox }.
//   record:        the deal or open house (read for sellerDoorCode only).
//   templateBody:  the body about to be filled in, so a template saved before the door code existed still
//                  carries it: with no {{doorCode}} in the body, the door code rides in {{lockbox}} instead
//                  ("Lockbox: Door code 4417#"). A body with neither, built from the separate Lockbox type /
//                  number / code chips Settings also offers, gets the door code in those chips ("Lockbox
//                  type: Door code", "Lockbox code: 4417#"), so the vendor is never left with no way in.
// With no door code, the lockbox values come back unchanged and doorCode is "" (its line is then dropped by
// stripEmptyFactLines, like any other empty fact line).
export function withDoorCode(lockboxFields, record, templateBody) {
  const f = lockboxFields && typeof lockboxFields === "object" ? lockboxFields : {};
  const base = {
    lockboxType: f.lockboxType || "",
    lockboxNumber: f.lockboxNumber || "",
    lockboxCode: f.lockboxCode || "",
    lockbox: f.lockbox || "",
  };
  const code = doorCodeOf(record);
  if (!code) return { ...base, doorCode: "" };
  const body = String(templateBody || "");
  const bodyHasDoorCode = DOOR_CODE_PLACEHOLDER_RE.test(body);
  const chipsCarryCode = !bodyHasDoorCode && !LOCKBOX_PLACEHOLDER_RE.test(body);
  return {
    lockboxType: chipsCarryCode ? "Door code" : "",
    lockboxNumber: "",
    lockboxCode: chipsCarryCode ? code : "",
    lockbox: bodyHasDoorCode ? "" : `Door code ${code}`,
    doorCode: code,
  };
}

// The same values made safe for a subject line. NO code of any kind goes in a subject: subjects sit in inbox
// lists, phone notifications and the Email Log, where a door code or a lockbox code would be read by more people
// and kept longer than the email itself. So {{doorCode}}, {{lockboxCode}} and {{lockbox}} (which carries the code
// inside its line) are always blank in a subject, whether or not the listing has a door code (2026-09-25 review:
// a saved custom subject carrying {{lockboxCode}} really did put a push-button code in the subject line).
// {{lockboxType}} and {{lockboxNumber}} name a box without opening it, so they stay, except with a door code on
// file, where the lockbox is not what anyone is being told about.
export function forSubjectLine(data) {
  if (!data) return data;
  const safe = { ...data, lockboxCode: "", lockbox: "", doorCode: "" };
  if (!data.doorCode) return safe;
  return { ...safe, lockboxType: "", lockboxNumber: "" };
}
